import * as React from 'react';
import { useSelector } from 'react-redux';

import { ApplicationState } from '../../../store';
import { Project } from '../../projects/models/Project';
import { ProjectDoc } from '../../projectDocs/models/ProjectDoc';
import { ProjectStatus } from '../../projects/models/ProjectStatus';
import { ProjectStatusVals } from '../../../helpers/mapping/ProjectStatus';
import { services } from '../../../services';
import { useBackgroundPromiseEffectByCondition } from '../../../helpers/hooks/useBackgroundPromiseEffectByCondition';

export interface ReportFiltersState {
  statuses: ProjectStatus[];
  selectedStatuses: ProjectStatus[];
  setSelectedStatuses: (v: ProjectStatus[]) => void;
  loadingStatuses: boolean;

  projects: Project[];
  selectedProject: Project | undefined;
  setSelectedProject: (v: Project | undefined) => void;
  loadingProjects: boolean;

  projectDocs: ProjectDoc[];
  selectedProjectDoc: ProjectDoc | undefined;
  setSelectedProjectDoc: (v: ProjectDoc | undefined) => void;
  loadingProjectDocs: boolean;
}

export function useReportFilters(): ReportFiltersState {
  const contractorIds = useSelector(
    (s: ApplicationState) => s.context.userContext.Contractors.map(c => c.Id ?? 0)
  );

  const [statuses, setStatuses] = React.useState<ProjectStatus[]>([]);
  const [selectedStatuses, setSelectedStatuses] = React.useState<ProjectStatus[]>([]);
  const [loadingStatuses, setLoadingStatuses] = React.useState(false);

  useBackgroundPromiseEffectByCondition(
    () => true,
    () => { setLoadingStatuses(true); return services.projectStatusService.fetch({ Sorts: [{ Field: 'Id', Desc: false }] }); },
    (res: ProjectStatus[]) => {
      setStatuses(res);
      setSelectedStatuses(res.filter(x => x.Id !== ProjectStatusVals.Completed));
      setLoadingStatuses(false);
    },
    []
  );

  const [projects, setProjects] = React.useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = React.useState<Project | undefined>();
  const [loadingProjects, setLoadingProjects] = React.useState(false);

  useBackgroundPromiseEffectByCondition(
    () => true,
    () => {
      setLoadingProjects(true);
      return services.projectDocService.fetch({
        ProjectStatusIds: selectedStatuses.map(x => x.Id ?? 0),
        ContractorIds: contractorIds,
        IsDeleted: false,
        ProjectIsDeleted: false,
        Sorts: [{ Field: 'Id', Desc: false }],
      });
    },
    (res: ProjectDoc[]) => {
      const seen = new Set<number>();
      const unique: Project[] = [];
      res.forEach(pd => {
        if (pd.Project && !seen.has(pd.Project.Id ?? 0)) {
          seen.add(pd.Project.Id ?? 0);
          unique.push(pd.Project);
        }
      });
      setProjects(unique);
      setLoadingProjects(false);
    },
    [selectedStatuses]
  );

  const [projectDocs, setProjectDocs] = React.useState<ProjectDoc[]>([]);
  const [selectedProjectDoc, setSelectedProjectDoc] = React.useState<ProjectDoc | undefined>();
  const [loadingProjectDocs, setLoadingProjectDocs] = React.useState(false);

  useBackgroundPromiseEffectByCondition(
    () => true,
    () => {
      setLoadingProjectDocs(true);
      return services.projectDocService.fetch({
        ProjectId: selectedProject?.Id,
        ContractorIds: contractorIds,
        IsDeleted: false,
        ProjectIsDeleted: false,
        Sorts: [{ Field: 'Id', Desc: false }],
      });
    },
    (res: ProjectDoc[]) => {
      setProjectDocs(res);
      setLoadingProjectDocs(false);
    },
    [selectedProject]
  );

  return {
    statuses, selectedStatuses, setSelectedStatuses, loadingStatuses,
    projects, selectedProject, setSelectedProject, loadingProjects,
    projectDocs, selectedProjectDoc, setSelectedProjectDoc, loadingProjectDocs,
  };
}
